import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/api-error-message'

import { policyCatalogKeys } from '../../api/policy-catalog-api'
import { policyOrderKeys } from '../../api/policy-order-api'
import { entityKeys } from '../../lib/entity-queries'
import {
  contentUploadPolicyApi,
  contentUploadPolicyKeys,
  type UploadLimitWrite,
} from '../../api/content-upload-policy-api'

const policyRefreshIntervalMs = 3000

export function useContentUploadPolicyResult(entityId: string) {
  return useQuery({
    queryKey: contentUploadPolicyKeys.result(entityId),
    queryFn: () => contentUploadPolicyApi.result(entityId),
    refetchInterval: (query) => {
      const status = query.state.data?.refreshStatus
      return status === 'Pending' || status === 'Running' ? policyRefreshIntervalMs : false
    },
  })
}

export function useUploadLimitDefinition(entityId: string) {
  return useQuery({
    queryKey: contentUploadPolicyKeys.own(entityId),
    queryFn: () => contentUploadPolicyApi.own(entityId),
  })
}

export function useSetUploadLimit(entityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (write: UploadLimitWrite) => contentUploadPolicyApi.set(entityId, write),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: contentUploadPolicyKeys.result(entityId) }),
        queryClient.invalidateQueries({ queryKey: entityKeys.settings(entityId) }),
        queryClient.invalidateQueries({ queryKey: policyCatalogKeys.all(entityId) }),
        queryClient.invalidateQueries({ queryKey: policyOrderKeys.all(entityId) }),
      ])
    },
  })
}

export function uploadLimitErrorMessage(error: unknown) {
  return apiErrorMessage(error, 'Could not save the upload limit. Try again.', {
    codes: {
      CONTENT_UPLOAD_POLICY_REVISION_CONFLICT: 'The upload limit changed. Reload the latest setting before saving again.',
      CONTENT_UPLOAD_POLICY_NOT_FOUND: 'The upload limit was removed. Reload the latest setting before saving again.',
      CONTENT_UPLOAD_POLICY_INVALID_VALUE: 'Enter a whole number from 1 through 10485760 bytes.',
      CONTENT_UPLOAD_POLICY_IDEMPOTENCY_CONFLICT: 'This save already identifies a different limit. Reload the latest setting before editing again.',
    },
  })
}
